/**
 * Remote (posthog-source) experiment assignment.
 *
 * Operating model: `assignment_source: 'posthog'` is the default for
 * experiments that need runtime operation — launch/stop, rollout percent, and
 * variant weights are operated with the console's native controls. For those
 * manifests the committed `status`, `rollout_percent`, and allocations are the
 * provisioning seed and documented intent; the console is runtime-authoritative
 * for lifecycle, rollout, and weights, and reconciliation stays dry-run (it
 * reports drift, never auto-applies it). `assignment_source: 'galapagos'`
 * remains available for assignment that must be network-free.
 *
 * The server keeps everything the console must not own: audience targeting is
 * pre-checked from the committed manifest before any remote call, QA
 * `subject_overrides` pin variants without evaluation, enrollment happens only
 * on an authoritative server evaluation (never a default/fallback), and a
 * returned arm the manifest does not declare is ignored — the console can only
 * operate arms that code declared. A code-side `status` edit away from
 * `running` hard-stops the experiment regardless of console state.
 */

import type { FeatureFlagProvider } from '@galapagos/observability';
import { analyticsSubjectIdSchema, flagKeySchema } from '@galapagos/product-analytics';
import {
  buildAssignment,
  isAudienceEligible,
  resolveExperimentAssignment,
  stableBucket,
  toExperimentSubjectKey,
  type ExperimentAssignmentContext,
} from './assignment';
import type { ExperimentAssignment, ExperimentManifest } from './schema';

export type RemoteAssignmentOutcome = 'assigned' | 'degraded' | 'skipped';

export type RemoteAssignmentReasonClass =
  | 'not_running'
  | 'ineligible'
  | 'invalid_subject'
  | 'invalid_flag_key'
  | 'no_provider'
  | 'provider_error'
  | 'used_default'
  | 'flag_disabled'
  | 'unknown_variant';

export interface RemoteAssignmentResolution {
  experiment_key: string;
  outcome: RemoteAssignmentOutcome;
  reason_class?: RemoteAssignmentReasonClass;
}

export interface RemoteAssignmentDeps {
  /**
   * Absent on a call site that has not wired a provider: server-owned
   * prechecks and `subject_overrides` still run (a pinned QA subject resolves
   * deterministically with no provider at all); only the remote evaluation
   * step degrades, as `no_provider`.
   */
  flagProvider?: FeatureFlagProvider;
  /**
   * Observability hook for each resolution (the app wires this to the
   * galapagos.experiments.remote_assignment telemetry event). Best-effort: a
   * throwing hook never affects resolution.
   */
  onResolution?: (resolution: RemoteAssignmentResolution) => void;
  now?: Date;
}

function notify(
  deps: Pick<RemoteAssignmentDeps, 'onResolution'>,
  resolution: RemoteAssignmentResolution,
): void {
  try {
    deps.onResolution?.(resolution);
  } catch {
    // The hook is observability-only; resolution semantics never depend on it.
  }
}

/**
 * Resolves a posthog-source manifest's assignment for one subject. Never
 * throws: every failure mode resolves to `null` (the base experience).
 */
export async function resolvePostHogExperimentAssignment(
  manifest: ExperimentManifest,
  context: ExperimentAssignmentContext,
  deps: RemoteAssignmentDeps,
): Promise<ExperimentAssignment | null> {
  const experimentKey = manifest.experiment_key;
  const settle = (
    outcome: RemoteAssignmentOutcome,
    reasonClass?: RemoteAssignmentReasonClass,
  ): null => {
    notify(deps, {
      experiment_key: experimentKey,
      outcome,
      ...(reasonClass ? { reason_class: reasonClass } : {}),
    });
    return null;
  };

  if (manifest.status !== 'running') return settle('skipped', 'not_running');
  if (!isAudienceEligible(manifest, context)) return settle('skipped', 'ineligible');

  const now = deps.now ?? new Date();
  const subjectKey = toExperimentSubjectKey(context.subjectKey);
  const overrideVariantKey = manifest.targeting.subject_overrides?.[subjectKey];
  const overrideVariant = overrideVariantKey
    ? manifest.variants.find((variant) => variant.key === overrideVariantKey)
    : undefined;
  if (overrideVariant) {
    const assignment = buildAssignment(manifest, overrideVariant, subjectKey, 0, now, context);
    notify(deps, { experiment_key: experimentKey, outcome: 'assigned' });
    return assignment;
  }

  const subject = analyticsSubjectIdSchema.safeParse(context.subjectKey);
  if (!subject.success) return settle('degraded', 'invalid_subject');
  const flagKey = flagKeySchema.safeParse(manifest.flag_key);
  if (!flagKey.success) return settle('degraded', 'invalid_flag_key');
  const flagProvider = deps.flagProvider;
  if (!flagProvider) return settle('degraded', 'no_provider');

  let evaluation;
  try {
    const snapshot = await flagProvider.evaluateAll({
      subjectId: subject.data,
      keys: [flagKey.data],
    });
    evaluation = snapshot.evaluations[manifest.flag_key];
  } catch {
    return settle('degraded', 'provider_error');
  }
  if (!evaluation) return settle('degraded', 'provider_error');
  if (evaluation.usedDefault || evaluation.source !== 'server') {
    return settle('degraded', 'used_default');
  }
  if (evaluation.value === false) return settle('degraded', 'flag_disabled');
  const variant =
    evaluation.value === true
      ? undefined
      : manifest.variants.find((candidate) => candidate.key === evaluation.value);
  if (!variant) return settle('degraded', 'unknown_variant');

  const bucket = stableBucket(
    `${manifest.experiment_key}:${manifest.version}:${manifest.targeting.salt}:${subjectKey}`,
  );
  const assignment = buildAssignment(manifest, variant, subjectKey, bucket, now, context);
  notify(deps, { experiment_key: experimentKey, outcome: 'assigned' });
  return assignment;
}

export interface AssignmentResolutionDeps {
  flagProvider?: FeatureFlagProvider;
  onResolution?: (resolution: RemoteAssignmentResolution) => void;
  now?: Date;
}

/**
 * The source-dispatching resolution seam every assignment call site consumes:
 * `galapagos` manifests resolve through the pure deterministic local hash,
 * `posthog` manifests through the remote resolver. Switching a manifest's mode
 * is a one-field edit; call sites never branch on the source themselves.
 */
export async function resolveAssignmentForManifest(
  manifest: ExperimentManifest,
  context: ExperimentAssignmentContext,
  deps: AssignmentResolutionDeps = {},
): Promise<ExperimentAssignment | null> {
  if (manifest.assignment_source === 'posthog') {
    // Always delegate — the resolver owns the precheck/override ordering, so a
    // providerless call site still honors status, audience rules, and pinned
    // subjects before anything can degrade as no_provider.
    return resolvePostHogExperimentAssignment(manifest, context, {
      ...(deps.flagProvider ? { flagProvider: deps.flagProvider } : {}),
      ...(deps.onResolution ? { onResolution: deps.onResolution } : {}),
      ...(deps.now ? { now: deps.now } : {}),
    });
  }
  return resolveExperimentAssignment(manifest, context, deps.now ?? new Date());
}
